import { Button, Heading, Text, Theme, useIsTheme } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { ReactNode, useEffect, useState } from 'react'
import { PaymentType, RecoverableTerminalErrorStatus } from '@afterpay/types'

import { DisabledModal, Head, ModuleContainer, NewPaymentMethod, ProfileCard } from '../../component'
import { Route, useSummaryRoute } from '../../config/router'
import { checkoutErrors, CheckoutPage, TopazError, CheckoutStatus } from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import {
  setCheckoutError,
  useAppDispatch,
  useAppRouter,
  setCheckoutStatus,
  useIsRecoverableFlow,
  setRecoverableFlowStatus,
  setRecoverableFlowPageReady,
  useIsConsumerLendingOrder,
  useSelectedPaymentType,
  resetPreferredPaymentMethodErrors,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { PaymentScheduleContainer } from '../../component/PaymentScheduleContainer/PaymentScheduleContainer'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useSubHeadingColor } from '../../utils/convergence'
import styles from './styles.module.css'

export const AddPaymentMethodPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const dispatch = useAppDispatch()
  const { instrument } = useMonitoringWithEnduringEventProperties()
  const { headingSize, textSize, buttonSize } = useCheckoutSizes()
  const summaryRoute = useSummaryRoute()
  const isRecoverableFlow = useIsRecoverableFlow()
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const paymentType = useSelectedPaymentType() ?? ('PBI' as PaymentType)

  const isAPTheme = useIsTheme(Theme.AP)
  const subHeadingColor = useSubHeadingColor()

  const handleTerminalError = (reason: TopazError): void => {
    dispatch(
      setCheckoutError({
        error: checkoutErrors[reason],
        errorCode: reason,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const handleSubmit = async (): Promise<void> => {
    if (isRecoverableFlow) {
      dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
      dispatch(setRecoverableFlowPageReady(true))
    } else {
      dispatch(setCheckoutStatus(CheckoutStatus.Started))
    }
    dispatch(resetPreferredPaymentMethodErrors())
    router.push(summaryRoute)
  }

  const handleContinue = instrument(TrackingEvent.PRESSED_ADD_FIRST_PAYMENT_CONTINUE, () => setFormSubmitted(!formSubmitted))

  const pageTitle = `${t('paymentMethod:addPaymentMethod:heading')} | ${t('common:brandName')}`
  let description: string
  let cta: string
  switch (paymentType) {
    case 'PCL':
      description = t('paymentMethod:addPaymentMethod:description:PCL')
      cta = t('paymentMethod:addPaymentMethod.cta.PCL')
      break
    case 'PAD':
      description = t('paymentMethod:addPaymentMethod:description:PAD')
      cta = t('paymentMethod:addPaymentMethod.cta.PAD')
      break
    default:
      description = t('paymentMethod:addPaymentMethod:description:PBI')
      cta = t('paymentMethod:addPaymentMethod.cta.PBI')
  }

  useEffect(() => {
    router.prefetch(Route.TERMINAL_ERROR)
  }, [])

  const renderSubheading = (): ReactNode => {
    if (!isAPTheme) {
      return (
        <div className={styles.subHeadingContainer}>
          <Text size={textSize} color={subHeadingColor}>
            {description}
          </Text>
        </div>
      )
    }

    return (
      <>
        <ModuleContainer nested padding={{ top: 'Large', horizontal: 'XLarge' }}>
          <ProfileCard />
        </ModuleContainer>
        <ModuleContainer nested padding={{ top: 'Large', horizontal: 'XLarge' }}>
          <Text size={textSize}>{description}</Text>
        </ModuleContainer>
      </>
    )
  }

  return (
    <>
      <Head title={pageTitle} />
      <Heading size={headingSize}>{t('paymentMethod:addPaymentMethod:heading')}</Heading>
      <PaymentScheduleContainer noBottomMargin />
      <ModuleContainer padding={{ vertical: 'Small' }} mintBorder={isAPTheme} showBorder={isAPTheme}>
        {renderSubheading()}
        <NewPaymentMethod
          onSuccess={async () => await handleSubmit()}
          onSubmit={(isSubmitting) => setIsSubmitting(isSubmitting)}
          formSubmitted={formSubmitted}
          onTerminalError={handleTerminalError}
          displayCreditCardMessage={isConsumerLendingOrder}
        />
        <ModuleContainer padding={isAPTheme ? { horizontal: 'XLarge', bottom: 'Large' } : 'None'} showBorder={isAPTheme} nested={isAPTheme}>
          <Button
            loading={isSubmitting}
            type='submit'
            padding='Fluid'
            size={buttonSize}
            disabled={isSubmitting}
            testNameSpace='add-payment-method'
            onClick={handleContinue}
          >
            {cta}
          </Button>
        </ModuleContainer>
      </ModuleContainer>
      <TrackPageViewed />
      {isSubmitting && <DisabledModal />}
    </>
  )
}
